import React, { useState } from 'react'
// MUI Imports
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Tooltip from '@mui/material/Tooltip'
import API from '../../../../../services/API'
import {
  Alert,
  Button,
  ButtonGroup,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import PermIdentityIcon from '@mui/icons-material/PermIdentity'
import BigModal from '../../../../../components/Modals/BigModal'
import AnDateField from '../../../../../components/DateTime/DateField'

const shiftRemovalStatuses = ['Leaver', 'Suspended']

const StatusChips = ({ user, setEmployee }) => {
  // const [isArchived, setisArchived] = useState(user.security.archived)
  const [canLogIn, setCanLogIn] = useState(user.security.canLogIn)
  const [canReceiveTasks, setCanReceiveTasks] = useState(
    user.employee?.canReceiveTasks,
  )
  const [status, setStatus] = useState(user.employee.status)
  const [statusMenuAnchorEl, setStatusMenuAnchorEl] = useState(null)
  const [requireLeaverInfo, setRequireLeaverInfo] = useState(false)
  const [leaveDate, setLeaveDate] = useState(null)
  const [leaveReason, setLeaveReason] = useState('')
  const [pendingStatusChange, setPendingStatusChange] = useState(null)
  const [submittingStatus, setSubmittingStatus] = useState(false)

  // const handleArchive = () => {
  //   API.post(`/employees/${user._id}/archive`, {
  //     archiveState: !isArchived,
  //   })
  //     .then((res) => res.status === 200 && setisArchived(!isArchived))
  //     .catch((e) => alert('Error: ' + e.message))
  // }

  const handleLoginAllowed = () => {
    API.post(`/employees/${user._id}/loginallowed`, {
      loginState: !canLogIn,
    })
      .then((res) => res.status === 200 && setCanLogIn(!canLogIn))
      .catch((e) => alert('Error: ' + e.message))
  }

  const handleReceiveTasks = () => {
    API.put('/employee', {
      updates: {
        _id: user._id,
        employee: {
          canReceiveTasks: !canReceiveTasks,
        },
      },
    })
      .then((res) => res.status === 200 && setCanReceiveTasks(!canReceiveTasks))
      .catch((e) => alert('Error: ' + e.message))
  }

  const getStatusUpdates = (newStatus) => {
    const updates = {
      _id: user._id,
      employee: {
        status: newStatus,
      },
    }

    if (newStatus === 'Leaver') {
      updates.employee = {
        status: newStatus,
        employementEndDate: leaveDate,
        reasonForLeaving: leaveReason,
      }
    }

    return updates
  }

  const submitStatusChange = (
    newStatus,
    updates,
    confirmRemoveUpcomingShifts = false,
  ) => {
    setSubmittingStatus(true)
    API.put('/employee', {
      updates,
      confirmRemoveUpcomingShifts,
    })
      .then((res) => {
        if (res.status === 200) {
          setStatus(newStatus)
          setEmployee((prev) => ({
            ...prev,
            employee: {
              ...prev.employee,
              ...updates.employee,
            },
          }))
          setStatusMenuAnchorEl(null)
          setRequireLeaverInfo(false)
          setPendingStatusChange(null)
        } else if (res.status === 409 && res.data?.upcomingShiftCount) {
          setPendingStatusChange({
            newStatus,
            updates,
            upcomingShiftCount: res.data.upcomingShiftCount,
          })
        }
      })
      .catch((e) => console.error(e))
      .finally(() => setSubmittingStatus(false))
  }

  const handleStatusChange = async (newStatus) => {
    if (newStatus === 'Leaver' && !leaveDate) {
      setRequireLeaverInfo(true)
      return
    }

    const updates = getStatusUpdates(newStatus)

    if (shiftRemovalStatuses.includes(newStatus) && status !== newStatus) {
      try {
        const res = await API.get(`/employees/${user._id}/upcoming-shifts/count`)

        if (res.status === 200 && res.data?.count > 0) {
          setPendingStatusChange({
            newStatus,
            updates,
            upcomingShiftCount: res.data.count,
          })
          setRequireLeaverInfo(false)
          setStatusMenuAnchorEl(null)
          return
        }
      } catch (e) {
        console.error(e)
      }
    }

    submitStatusChange(newStatus, updates)
  }

  return (
    <Box display="flex" gap={1} flexWrap="wrap">
      {user.employee?.pinNumber && (
        <Tooltip title="Staff ID / PIN">
          <Chip
            label={user.employee.pinNumber}
            color="anMainBlue"
            size="small"
            sx={{ borderRadius: 1 }}
            icon={<PermIdentityIcon />}
          />
        </Tooltip>
      )}
      {/* <Tooltip title="Click to change">
        <Chip
          label={isArchived ? 'Archived' : 'Active'}
          color={isArchived ? 'error' : 'success'}
          size="small"
          sx={{ borderRadius: 1, cursor: 'pointer' }}
          onClick={handleArchive}
        />
      </Tooltip> */}
      <Tooltip title="Click to change">
        <Chip
          label={canLogIn ? 'Login Enabled' : 'Login Disabled'}
          color={canLogIn ? 'success' : 'error'}
          size="small"
          sx={{ borderRadius: 1, cursor: 'pointer' }}
          onClick={handleLoginAllowed}
        />
      </Tooltip>
      <Chip
        label={status}
        size="small"
        color={
          status === 'Applicant'
            ? 'warning'
            : status === 'Live'
              ? 'success'
              : status === 'New Starter'
                ? 'info'
                : status === 'Leaver'
                  ? 'error'
                  : status === 'Suspended'
                    ? 'warning'
                    : 'primary'
        }
        sx={{ borderRadius: 1 }}
        onClick={(e) => setStatusMenuAnchorEl(e.currentTarget)}
      />
      <Menu
        open={!!statusMenuAnchorEl}
        onClose={() => setStatusMenuAnchorEl(null)}
        anchorEl={statusMenuAnchorEl}
      >
        <MenuItem onClick={() => handleStatusChange('New Starter')}>
          New Starter
        </MenuItem>
        <MenuItem onClick={() => handleStatusChange('Live')}>Live</MenuItem>
        <MenuItem onClick={() => handleStatusChange('Suspended')}>
          Suspended
        </MenuItem>
        <MenuItem onClick={() => setRequireLeaverInfo(true)}>Leaver</MenuItem>
      </Menu>
      <Tooltip title="Click to enable/disable tasks for this user">
        <Chip
          label="Tasks"
          color={canReceiveTasks ? 'success' : 'primary'}
          size="small"
          sx={{ borderRadius: 1, cursor: 'pointer' }}
          onClick={handleReceiveTasks}
        />
      </Tooltip>
      <BigModal
        open={!!requireLeaverInfo}
        onClose={() => setRequireLeaverInfo(false)}
      >
        <Stack
          gap={1}
          component="form"
          onSubmit={(e) => {
            e.preventDefault()
            handleStatusChange('Leaver')
          }}
          padding={1}
        >
          <AnDateField
            required
            date={leaveDate}
            onChange={(newDate) => setLeaveDate(newDate)}
            label="Leave Date"
          />
          <TextField
            value={leaveReason}
            onChange={(e) => setLeaveReason(e.target.value)}
            label="Leave Reason"
            autoComplete="off"
            helperText="(Optional)"
          />
          <ButtonGroup fullWidth>
            <Button
              variant="contained"
              type="button"
              onClick={() => setRequireLeaverInfo(false)}
              color="error"
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              type="submit"
              color="success"
              disabled={submittingStatus}
            >
              Submit
            </Button>
          </ButtonGroup>
        </Stack>
      </BigModal>
      <Dialog
        open={!!pendingStatusChange}
        onClose={() => !submittingStatus && setPendingStatusChange(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Remove upcoming shifts?</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Alert severity="warning">
              Changing this employee to {pendingStatusChange?.newStatus} will
              remove them from {pendingStatusChange?.upcomingShiftCount}{' '}
              {pendingStatusChange?.upcomingShiftCount === 1
                ? 'upcoming shift'
                : 'upcoming shifts'}.
            </Alert>
            <Typography variant="body2" color="text.secondary">
              The shifts will remain in the rota, but this employee will no
              longer be assigned to them.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setPendingStatusChange(null)}
            disabled={submittingStatus}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            disabled={submittingStatus}
            onClick={() =>
              submitStatusChange(
                pendingStatusChange.newStatus,
                pendingStatusChange.updates,
                true,
              )
            }
          >
            Confirm and remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default StatusChips
